import { EditMode, FieldTypeCategory, getFieldType } from '@databases/fields';
import { type DefaultRootState } from '@databases/redux';
import { type DatabaseAdapter } from '@databases/selections-api-types';
import {
  type DatabaseEntry,
  type DatabaseField,
  type DatabaseWithoutEntries,
} from '@databases/types/database';
import { getReferencedStatusContentRefs } from '@databases/utils/database/field-types/pageStatus';
import { getDatabaseValue } from '@databases/utils/database/utils';

/**
 * Shape of database state as provided by Redux selectors.
 *
 * Extends DatabaseWithoutEntries so that the snapshot is assignable to external
 * helpers (getDatabaseValue, editMode, getReferencedStatusContentRefs) that
 * expect the full database shape. The runtime objects are always full Database
 * instances produced by selectDatabaseDraftByContentId.
 *
 * Note: `fields` contains view-filtered field IDs, while `fieldsById` contains
 * all fields. Use Object.keys(fieldsById) to get all field IDs.
 */
export interface DatabaseSnapshot extends DatabaseWithoutEntries {
  entries: readonly string[];
  entriesById: Readonly<Record<string, DatabaseEntry>>;
}

export interface ReduxDatabaseAdapterOptions {
  getContentId: () => string;
  getDatabaseSnapshot: (contentId: string) => DatabaseSnapshot | undefined;
  getState: () => DefaultRootState;
}

/**
 * DatabaseAdapter implementation that reads from Redux state.
 *
 * Uses a snapshot getter pattern to ensure fresh data on each access,
 * allowing the SelectionsApi to work with the current view-filtered state.
 *
 * This adapter is responsible for database *data access* only (fields,
 * entries, snapshots, cell editability). Permission checks live in
 * DatabasePermissionsManager — see @databases/permissions.
 */
export class ReduxDatabaseAdapter implements DatabaseAdapter {
  #getContentId: () => string;
  #getDatabaseSnapshot: (contentId: string) => DatabaseSnapshot | undefined;
  #getState: () => DefaultRootState;

  constructor(options: ReduxDatabaseAdapterOptions) {
    this.#getContentId = options.getContentId;
    this.#getDatabaseSnapshot = options.getDatabaseSnapshot;
    this.#getState = options.getState;
  }

  #getDatabase(): DatabaseSnapshot | undefined {
    return this.#getDatabaseSnapshot(this.#getContentId());
  }

  getVisibleFieldIds(): string[] {
    const fields = this.#getDatabase()?.fields;

    return fields ? [...fields] : [];
  }

  getVisibleEntryIds(): string[] {
    const entries = this.#getDatabase()?.entries;

    return entries ? [...entries] : [];
  }

  getFieldById(fieldId: string): DatabaseField | undefined {
    return this.#getDatabase()?.fieldsById[fieldId];
  }

  getEntryById(entryId: string): DatabaseEntry | undefined {
    return this.#getDatabase()?.entriesById[entryId];
  }

  getSpaceKey(): string {
    return this.#getDatabase()?.space.key ?? '';
  }

  getAllFieldIds(): string[] {
    const fieldsById = this.#getDatabase()?.fieldsById;

    return fieldsById ? Object.keys(fieldsById) : [];
  }

  canEditValue(fieldId: string, entryId: string): boolean {
    const database = this.#getDatabase();

    if (!database) {
      return false;
    }

    const field = database.fieldsById[fieldId];

    if (!field) {
      return false;
    }

    const entry = database.entriesById[entryId] ?? database.defaultEntry;
    const state = this.#getState();
    const value = getDatabaseValue(database, field, entry);

    return (
      getFieldType(field.type).editMode({ database, field, entry, value }, state) !==
      EditMode.READ_ONLY
    );
  }

  canBulkEditValue(fieldId: string, entryIds: string[]): boolean {
    const database = this.#getDatabase();

    if (!database) {
      return false;
    }

    const field = database.fieldsById[fieldId];

    if (!field) {
      return false;
    }

    const fieldType = getFieldType(field.type);
    const uniqueEntryIds = [...new Set(entryIds)];
    const state = this.#getState();

    for (const entryId of uniqueEntryIds) {
      const entry = database.entriesById[entryId] ?? database.defaultEntry;
      const value = getDatabaseValue(database, field, entry);

      if (
        fieldType.editMode({ database, field, entry, value }, state) ===
        EditMode.READ_ONLY
      ) {
        return false;
      }
    }

    return this.#allEntriesSharePageContext(
      fieldType,
      database,
      field,
      uniqueEntryIds,
      state
    );
  }

  #allEntriesSharePageContext(
    fieldType: ReturnType<typeof getFieldType>,
    database: DatabaseSnapshot,
    field: DatabaseField,
    entryIds: string[],
    state: DefaultRootState
  ): boolean {
    if (fieldType.category !== FieldTypeCategory.PAGE) {
      return true;
    }

    const allRefs = entryIds.flatMap((entryId) => {
      const entry = database.entriesById[entryId] ?? database.defaultEntry;

      return getReferencedStatusContentRefs({ database, field, entry }, state);
    });

    if (allRefs.length === 0) {
      return true;
    }

    return allRefs.every((ref) => ref.contentId === allRefs[0].contentId);
  }
}
